import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Mail, Server, Lock, User, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

export function SMTPSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasSettings, setHasSettings] = useState(false);
  const [settingsId, setSettingsId] = useState("");
  const [settings, setSettings] = useState({
    smtp_host: "",
    smtp_port: 2525,
    smtp_username: "",
    smtp_password: "",
    from_email: "",
    from_name: "Onsite Technology Solutions",
    use_tls: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${getBaseURL()}/api/smtp-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings({
        smtp_host: response.data.smtp_host,
        smtp_port: response.data.smtp_port,
        smtp_username: response.data.smtp_username,
        smtp_password: response.data.smtp_password,
        from_email: response.data.from_email,
        from_name: response.data.from_name,
        use_tls: response.data.use_tls
      });
      setSettingsId(response.data.id);
      setHasSettings(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setHasSettings(false);
      } else {
        console.error("Load settings error:", error);
        toast.error("Failed to load SMTP settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!settings.smtp_host || !settings.smtp_port || !settings.smtp_username || 
        !settings.smtp_password || !settings.from_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("auth_token");

      if (hasSettings && settingsId) {
        // Update existing settings
        await axios.put(
          `${getBaseURL()}/api/smtp-settings/${settingsId}`,
          settings,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new settings
        const response = await axios.post(
          `${getBaseURL()}/api/smtp-settings`,
          settings,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSettingsId(response.data.id);
        setHasSettings(true);
      }

      toast.success("SMTP settings saved successfully");
    } catch (error) {
      console.error("Save settings error:", error);
      toast.error("Failed to save SMTP settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!hasSettings) {
      toast.error("Please save settings first before testing");
      return;
    }

    try {
      setTesting(true);
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(
        `${getBaseURL()}/api/smtp-settings/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Test email sent successfully to ${response.data.sent_to}`);
    } catch (error) {
      console.error("Test email error:", error);
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">SMTP Settings</h1>
        <p className="text-gray-600 mt-2">Configure email server settings for system notifications</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            SMTP2Go Configuration
          </CardTitle>
          <CardDescription>
            Enter your SMTP2Go server details to enable email notifications for support tickets
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            {/* SMTP Host */}
            <div className="space-y-2">
              <Label htmlFor="smtp_host">SMTP Host *</Label>
              <Input
                id="smtp_host"
                type="text"
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="mail.smtp2go.com"
                required
              />
              <p className="text-sm text-gray-500">Usually: mail.smtp2go.com</p>
            </div>

            {/* SMTP Port */}
            <div className="space-y-2">
              <Label htmlFor="smtp_port">SMTP Port *</Label>
              <Input
                id="smtp_port"
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 2525 })}
                required
              />
              <p className="text-sm text-gray-500">Common ports: 2525, 587, or 25</p>
            </div>

            {/* SMTP Username */}
            <div className="space-y-2">
              <Label htmlFor="smtp_username" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                SMTP Username *
              </Label>
              <Input
                id="smtp_username"
                type="text"
                value={settings.smtp_username}
                onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                placeholder="Your SMTP2Go username"
                required
              />
            </div>

            {/* SMTP Password */}
            <div className="space-y-2">
              <Label htmlFor="smtp_password" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                SMTP Password *
              </Label>
              <Input
                id="smtp_password"
                type="password"
                value={settings.smtp_password}
                onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                placeholder="Your SMTP2Go password"
                required
              />
            </div>

            {/* From Email */}
            <div className="space-y-2">
              <Label htmlFor="from_email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                From Email *
              </Label>
              <Input
                id="from_email"
                type="email"
                value={settings.from_email}
                onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                placeholder="support@yourdomain.com"
                required
              />
              <p className="text-sm text-gray-500">This email will appear as the sender</p>
            </div>

            {/* From Name */}
            <div className="space-y-2">
              <Label htmlFor="from_name">From Name</Label>
              <Input
                id="from_name"
                type="text"
                value={settings.from_name}
                onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                placeholder="Onsite Technology Solutions"
              />
            </div>

            {/* Use TLS */}
            <div className="flex items-center justify-between py-2 border rounded-md px-4">
              <div className="space-y-0.5">
                <Label htmlFor="use_tls">Use TLS/STARTTLS</Label>
                <p className="text-sm text-gray-500">Enable secure connection (recommended)</p>
              </div>
              <Switch
                id="use_tls"
                checked={settings.use_tls}
                onCheckedChange={(checked) => setSettings({ ...settings, use_tls: checked })}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>

              {hasSettings && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleTest}
                  disabled={testing || saving}
                >
                  {testing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            SMTP2Go Setup Information
          </CardTitle>
        </CardHeader>
        <CardContent className="text-blue-900 space-y-2 text-sm">
          <p>To get your SMTP2Go credentials:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Sign up for a free account at <a href="https://www.smtp2go.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">smtp2go.com</a></li>
            <li>Verify your sender email address</li>
            <li>Go to Settings → SMTP Users to create SMTP credentials</li>
            <li>Use the host: <code className="bg-blue-100 px-1 rounded">mail.smtp2go.com</code></li>
            <li>Use port: <code className="bg-blue-100 px-1 rounded">2525</code> (or 587)</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
